/**
 * Business API Client
 * Handles API calls for business/branch management
 */

import axios, { getBusinessId } from '@/lib/api/axios-instance';

const BASE_URL = '/api/v2/business';

export interface Business {
  business_id: number;
  business_name: string;
  business_type?: string;
  is_active: boolean;
  account_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface BusinessListResponse {
  success: boolean;
  data: Business[];
}

export interface Address {
  street?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
}

export interface BusinessDetail extends Business {
  id?: string;
  customer_id?: string;
  description?: string | null;
  business_description?: string | null;
  tax_id?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  address?: Address | string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  status?: string | null;
}

export interface UpdateBusinessRequest {
  business_name?: string;
  business_type?: string;
  tax_id?: string | null;
  business_description?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  address?: Address | null;
  website?: string | null;
}

export const businessesAPI = {
  /**
   * Get all businesses for the current user
   */
  async getAll(): Promise<Business[]> {
    const response = await axios.get<BusinessListResponse>(
      `${BASE_URL}/businesses`
    );
    return response.data.data;
  },

  /**
   * Get single business by ID
   */
  async getById(businessId: string): Promise<BusinessDetail> {
    const response = await axios.get<{ success: boolean; data: BusinessDetail }>(
      `${BASE_URL}/businesses/${businessId}`
    );
    return response.data.data;
  },

  /**
   * Get detail of current business (uses getBusinessId helper)
   */
  async getCurrent(): Promise<BusinessDetail> {
    const businessId = getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not found. Please log in again.');
    }
    return businessesAPI.getById(businessId);
  },

  /**
   * Update business information
   */
  async update(
    businessId: string,
    data: UpdateBusinessRequest
  ): Promise<BusinessDetail> {
    const response = await axios.put<{ success: boolean; data: BusinessDetail }>(
      `${BASE_URL}/businesses/${businessId}`,
      data
    );
    return response.data.data;
  },
};

export default businessesAPI;
